import { AppConfirmDeleteDialog, AppIconButton, AppLoadingSpinner, AppTextarea } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import type { GetInquiryCoordinationsResponse } from 'backend/types'
import { RefreshCw, Send } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { FIELD_MAX_LENGTHS } from 'shared'
import { CoordinationItem } from './components/CoordinationItem'
import { useCoordinationCreate } from './hooks/useCoordinationCreate'
import { useDeleteInquiryCoordination } from './hooks/useDeleteInquiryCoordination'
import { useInquiryCoordinations } from './hooks/useInquiryCoordinations'
import { useUpdateInquiryCoordination } from './hooks/useUpdateInquiryCoordination'

type CoordinationTabProps = {
  inquiryId: number
  isInquiryCompleted: boolean
  onDirtyChange: (isDirty: boolean) => void
}

type Coordination = GetInquiryCoordinationsResponse['inquiry_coordinations'][number]

export function CoordinationTab({ inquiryId, isInquiryCompleted, onDirtyChange }: CoordinationTabProps) {
  const { data, isLoading, isRefetching, refetch } = useInquiryCoordinations(inquiryId)
  const updateCoordination = useUpdateInquiryCoordination(inquiryId)
  const deleteCoordination = useDeleteInquiryCoordination(inquiryId)

  const coordinations = data?.inquiry_coordinations ?? []
  const [deleteTarget, setDeleteTarget] = useState<Coordination | null>(null)
  const [editingCount, setEditingCount] = useState(0)

  const { newContent, contentError, canSubmit, isPending, handleSubmit, handleKeyDown, handleContentChange } =
    useCoordinationCreate(inquiryId)

  const isDirty = newContent.trim().length > 0 || editingCount > 0

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const handleEditingChange = useCallback((isEditing: boolean) => {
    setEditingCount((prev) => prev + (isEditing ? 1 : -1))
  }, [])

  const handleEdit = useCallback(
    async (id: number, content: string) => {
      await updateCoordination.mutateAsync({ id, content })
    },
    [updateCoordination.mutateAsync],
  )

  const handleDeleteRequest = useCallback(
    (id: number) => {
      setDeleteTarget((prev) => coordinations.find((c) => c.id === id) ?? prev)
    },
    [coordinations],
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    try {
      await deleteCoordination.mutateAsync(deleteTarget.id)
      setDeleteTarget(null)
    } catch {}
  }, [deleteTarget, deleteCoordination.mutateAsync])

  return (
    <div className='flex h-full flex-col'>
      <div className='flex-1 space-y-3 overflow-auto p-4'>
        {isLoading ? (
          <div className='flex justify-center py-4'>
            <AppLoadingSpinner />
          </div>
        ) : coordinations.length === 0 ? (
          <p className='text-center text-sm text-muted-foreground'>調整状況はありません</p>
        ) : (
          coordinations.map((coordination) => (
            <CoordinationItem
              key={coordination.id}
              coordination={coordination}
              onEdit={handleEdit}
              onDelete={handleDeleteRequest}
              onEditingChange={handleEditingChange}
              isInquiryCompleted={isInquiryCompleted}
            />
          ))
        )}
      </div>
      <div className='border-t p-4'>
        <div className='relative'>
          <AppTextarea
            value={newContent}
            label='調整状況を入力'
            placeholder={isInquiryCompleted ? '完了済みのため編集できません' : undefined}
            helpText={isInquiryCompleted ? undefined : 'Enter/改行、Ctrl+Enter/送信'}
            onChange={(e) => handleContentChange(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={isInquiryCompleted || isPending}
            maxLength={FIELD_MAX_LENGTHS.INQUIRY_COORDINATION_CONTENT}
            showCharCount={!isInquiryCompleted}
            limitHeight='sm'
            error={contentError ?? undefined}
            className='pr-20'
          />
          <div className='absolute right-2 bottom-6 flex gap-1'>
            <AppIconButton description='送信' onClick={handleSubmit} disabled={isInquiryCompleted || !canSubmit}>
              <Send />
            </AppIconButton>
            <AppIconButton
              description='再読み込み'
              onClick={() => refetch()}
              disabled={isInquiryCompleted || isRefetching}
            >
              <RefreshCw />
            </AppIconButton>
          </div>
        </div>
      </div>

      <AppConfirmDeleteDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
        message='この調整状況を削除しますか？'
        subtitle={
          deleteTarget ? deleteTarget.content.slice(0, 50) + (deleteTarget.content.length > 50 ? '…' : '') : undefined
        }
        onConfirm={handleDeleteConfirm}
      />
    </div>
  )
}
